/** @jsxImportSource theme-ui */
import { useState } from 'react';
import { Box, Button, Flex, Text } from 'theme-ui';
import { ChevronDownIcon, ChevronUpIcon, XIcon } from '@heroicons/react/solid';
import Select from '@/components/Select';
import { Company } from './types';

export type BulkAction = { value: string; label: string };

export const BULK_ACTIONS: BulkAction[] = [
    { value: 'add-feature-flag', label: 'Configure feature flags' },
];

interface Props {
    selectedCompanies: Company[];
    onClear: () => void;
    onRemove: (id: string) => void;
    onStart: (action: string) => void;
}

const BulkActionBar = ({ selectedCompanies, onClear, onRemove, onStart }: Props) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedAction, setSelectedAction] = useState<BulkAction | null>(null);

    return (
        <Box
            sx={{
                backgroundColor: 'background',
                borderRadius: 'sm',
                p: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'gray.2',
            }}
        >
            <Flex sx={{ alignItems: 'center', gap: 2 }}>
                <Button
                    variant="plain"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    <Text sx={{ fontSize: '12px', fontWeight: 500 }}>
                        {selectedCompanies.length} selected
                    </Text>
                    {isExpanded ? (
                        <ChevronUpIcon width={14} height={14} />
                    ) : (
                        <ChevronDownIcon width={14} height={14} />
                    )}
                </Button>
                <Button
                    variant="plain"
                    onClick={onClear}
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <XIcon width={14} height={14} />
                </Button>
                <Flex sx={{ marginLeft: 'auto', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ minWidth: '200px' }}>
                        <Select
                            options={BULK_ACTIONS}
                            value={selectedAction}
                            onChange={(option) => setSelectedAction(option as BulkAction | null)}
                            placeholder="Select action"
                            isClearable
                        />
                    </Box>
                    <Button
                        disabled={!selectedAction}
                        onClick={() => selectedAction && onStart(selectedAction.value)}
                        sx={{ opacity: !selectedAction ? 0.5 : 1 }}
                    >
                        Start
                    </Button>
                </Flex>
            </Flex>
            {isExpanded && (
                <Box sx={{ mt: 2, maxHeight: '200px', overflowY: 'auto' }}>
                    {selectedCompanies.map((c) => (
                        <Flex
                            key={c.Id}
                            sx={{
                                fontSize: '12px',
                                py: 1,
                                gap: 2,
                                alignItems: 'center',
                                borderBottom: '1px solid',
                                borderColor: 'gray.1',
                            }}
                        >
                            <Text sx={{ color: 'gray.5', fontFamily: 'monospace' }}>{c.Id}</Text>
                            <Text>{c.Name}</Text>
                            <Button
                                variant="plain"
                                onClick={() => c.Id && onRemove(c.Id)}
                                sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', p: 0 }}
                            >
                                <XIcon width={12} height={12} />
                            </Button>
                        </Flex>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default BulkActionBar;
